import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {filter, map, Subscription, take} from "rxjs";
import {GeneralPartnerValuationActions, TransactionSummaryActions} from "../../store/asset.actions";
import {
    selectBaselineGeneralPartnerValuation,
    selectExitsEdit,
    selectGeneralPartnerValuation,
    selectIsAnyAssetFormInEditMode,
    selectRelatedFund,
    selectRelatedFundValuationId,
    selectRelatedReportId,
    selectReportDate,
    selectSelectedAssetId,
    selectTransactionSummary,
    selectTransactionSummarySaveState
} from "../../store/asset/asset.selectors";
import {selectPreferredAssetValuationForAssetAndFundValuation} from "../../store/asset-valuation/asset-valuation.selectors";
import {DateUtil} from "../../../shared/utils/date-util";
import {ConfirmationComponent} from "../../../shared/components/confirmation/confirmation.component";
import {MatDialog} from "@angular/material/dialog";
import {TraceableDate} from "../../../shared/model/traceable";
import {EMPTY_FUND_INVESTMENT, FundInvestment} from "../../../fund/models/fund-investement";

@Component({
    selector: "valumize-asset-exit-information",
    templateUrl: "./asset-exit-information.component.html",
    styleUrls: ["./asset-exit-information.component.scss"],
    standalone: false
})
export class AssetExitInformationComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);

    subscriptions: Subscription[] = [];
    // Tracked separately (not pushed into subscriptions[]) because dispatchSave() can re-create
    // this one on every save click — replacing it here cancels any still-pending previous wait,
    // so a rapid re-save can't double-dispatch TransactionSummary.save once fundInvestment$ resolves.
    private pendingTransactionSummarySaveSubscription?: Subscription;
    // What the server currently holds, so dispatchSave() can tell an actual edit from an untouched
    // field and skip the second write when nothing changed.
    private persistedTransactionSummaryText = "";

    assetValuation$ = this.store.select(selectPreferredAssetValuationForAssetAndFundValuation);
    fundInvestment$ = this.store.select(selectGeneralPartnerValuation);
    baselineFundInvestment: FundInvestment = EMPTY_FUND_INVESTMENT;
    assetId$ = this.store.select(selectSelectedAssetId);
    fundReportId$ = this.store.select(selectRelatedReportId);
    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    relatedFund$ = this.store.select(selectRelatedFund);
    transactionSummary$ = this.store.select(selectTransactionSummary);
    transactionSummarySaveState$ = this.store.select(selectTransactionSummarySaveState);

    exitIsEditable = false;
    isDataset = false;
    reportDate?: TraceableDate;
    fundValuationId?: number;
    mostRecentModDate?: string;
    warningMessage = "";

    exitForm = this.formBuilder.group({
        entryDate: this.formBuilder.control<Date | null>(null),
        effectiveExitDate: this.formBuilder.control<Date | null>(null),
        exitDateAssumption: this.formBuilder.control<Date | null>(null),
        isRealized: this.formBuilder.control<boolean | undefined>(undefined, {nonNullable: true, validators: Validators.required})
    }, {validators: this.effectiveExitDateRangeValidator()});

    transactionSummaryForm = this.formBuilder.group({
        text: this.formBuilder.control("", {nonNullable: true})
    });

    ngOnInit() {
        this.subscriptions.push(this.fundInvestment$.pipe(map((fundInvestment) => {
            const entryDate = DateUtil.toJsDate(fundInvestment.data.investmentDate.date);
            const effectiveExitDate = DateUtil.toJsDate(fundInvestment.data.exitDate.date);
            const isRealized = fundInvestment.data.isRealized.value;
            this.exitForm.patchValue({entryDate, effectiveExitDate, isRealized});

            this.mostRecentModDate = DateUtil.toIsoDate(DateUtil.mostRecent(
                DateUtil.fromIsoTimestamp(fundInvestment.data.investmentDate.modDate),
                DateUtil.fromIsoTimestamp(fundInvestment.data.exitDate.modDate),
                DateUtil.fromIsoTimestamp(fundInvestment.data.isRealized.modDate))
            );
        })).subscribe());

        this.subscriptions.push(
            this.store.select(selectBaselineGeneralPartnerValuation).pipe(map((baselineFundInvestment) => {
                if (baselineFundInvestment.data.id) {
                    this.isDataset = true;
                    this.baselineFundInvestment = baselineFundInvestment.data;
                } else {
                    this.isDataset = false;
                }
            })).subscribe());

        this.subscriptions.push(this.store.select(selectRelatedFundValuationId).pipe(map((fundValuationId) => {
            this.fundValuationId = fundValuationId;
        })).subscribe());

        this.subscriptions.push(this.store.select(selectReportDate).pipe(map((reportDate) => {
            this.reportDate = reportDate;
        })).subscribe());

        this.subscriptions.push(this.store.select(selectExitsEdit).pipe(map((isEditable) => {
            this.exitIsEditable = isEditable;
            if (isEditable) {
                this.exitForm.enable();
                this.transactionSummaryForm.enable();
            } else {
                // Cancel only flips this flag and never touches generalPartnerValuation.data, so the
                // store emits nothing and the patch subscription below cannot fire. Restore the
                // persisted text here or the abandoned draft survives, invisibly (the read-only view
                // renders from the store), and the next Save writes it — now certain to, since
                // dispatchSave treats a differing draft as a genuine edit.
                this.transactionSummaryForm.patchValue({text: this.persistedTransactionSummaryText});
                this.exitForm.disable();
                this.transactionSummaryForm.disable();
            }
        })).subscribe());

        this.subscriptions.push(this.transactionSummary$.pipe(
            map((transactionSummary) => {
                // Always track what is persisted, even mid-edit: dispatchSave() compares against it
                // to decide whether the summary needs saving at all.
                // ?? "" so a never-set summary compares equal to the empty form control rather
                // than counting as an edit on every save.
                this.persistedTransactionSummaryText = transactionSummary.text ?? "";

                // But only patch the form when not editing, so an emission (e.g. the realization
                // save overwriting fundInvestment.data) can't clobber the user's unsaved input —
                // mirrors the guard used by the recentFinancialPerformance/developmentsSinceInvestment editors.
                if (!this.exitIsEditable) {
                    this.transactionSummaryForm.patchValue({text: transactionSummary.text});
                }
            })).subscribe());
    }

    editMode = () => {
        this.store.dispatch(GeneralPartnerValuationActions.editRealization());
        this.store.dispatch(TransactionSummaryActions.edit());
    };

    closeWarning() {
        this.warningMessage = "";
    }

    save() {
        if (this.exitForm.getRawValue().isRealized && this.reportDate) {
            const dialogRef = this.dialog.open(ConfirmationComponent);
            dialogRef.componentInstance.confirmMessage = "Change to Realized - Confirm Action";
            dialogRef.componentInstance.htmlContent = `
                <p>Changing investment to 'Realized' will:</p>
                <ul>
                    <li>set the NAV for this company in this report to 0</li>
                    ${this.fundValuationId ? `<li>delete all valuations belonging to this report</li>` : ``}
                    <li>set the ownership stake of the investment to 0</li>
                    <li>delete the fund as a shareholder of this asset</li>
                </ul>
                <p>Do you want to proceed?</p>`;

            dialogRef.afterClosed().pipe(map(result => {
                if (result) {
                    this.dispatchSave();
                }
            })).subscribe();
            return;
        }
        this.dispatchSave();
    }

    dispatchSave() {
        const isRealized = this.exitForm.getRawValue().isRealized;
        const effectiveExitDate = this.exitForm.getRawValue().effectiveExitDate;
        const entryDate = this.exitForm.getRawValue().entryDate;
        // Captured now, before dispatching — the realization save's response overwrites the
        // whole fundInvestment.data object (including the not-yet-saved transactionSummary),
        // which re-patches transactionSummaryForm back to the OLD text via the subscription
        // above. Reading getRawValue() again after that point would send back the stale value.
        const transactionSummaryValue = this.transactionSummaryForm.getRawValue();

        if (this.exitForm.valid) {
            this.store.dispatch(GeneralPartnerValuationActions.saveRealization({
                isRealized: isRealized ?? false,
                exitDate: DateUtil.toIsoDate(effectiveExitDate) ?? undefined,
                investmentDate: DateUtil.toIsoDate(entryDate)! ?? undefined
            }))

            // Both writes are narrow PATCHes now, but each still reads the investment and writes
            // every column back from that read (the entity has no @Version), so firing them
            // together would let one lose the other's changes. Chain the summary save behind the
            // realization save instead of dispatching both at once.
            //
            // Only on "SAVED": accepting "ERROR" here meant a failed exit save still dispatched
            // this, and since loadError blanks data to EMPTY_FUND_INVESTMENT the effect then found
            // no investment id and replaced the real server error with "please refresh the page".
            //
            // Only when the text changed: it used to fire on every exit-date save, so an untouched
            // summary caused a second write and a second fund-report recalculation each time.
            this.pendingTransactionSummarySaveSubscription?.unsubscribe();
            if (transactionSummaryValue.text !== this.persistedTransactionSummaryText) {
                this.pendingTransactionSummarySaveSubscription = this.fundInvestment$.pipe(
                    filter((fundInvestment) => fundInvestment.status === "SAVED" || fundInvestment.status === "ERROR"),
                    take(1)
                ).subscribe((fundInvestment) => {
                    if (fundInvestment.status === "SAVED") {
                        this.store.dispatch(TransactionSummaryActions.save(transactionSummaryValue));
                    }
                });
            }

            const reportDateToCompare = this.reportDate;
            if (isRealized === false && effectiveExitDate && reportDateToCompare && effectiveExitDate < reportDateToCompare) {
                this.warningMessage = "Discrepancy: Company marked as unrealized but exit date is before the report date.";
            }

            if (isRealized === true && effectiveExitDate && reportDateToCompare && effectiveExitDate > reportDateToCompare) {
                this.warningMessage = "Discrepancy: Company marked as realized but exit date is after the report date.";
            }
        }
    }

    cancel = () => {
        this.store.dispatch(GeneralPartnerValuationActions.cancelRealization());
        this.store.dispatch(TransactionSummaryActions.cancel());
    };

    effectiveExitDateRangeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const group = control as FormGroup;
            const entryDate = group.get("entryDate")?.value;
            const effectiveExitDate = group.get("effectiveExitDate")?.value;

            if (!entryDate || !effectiveExitDate) {
                return null;
            }

            const isBeforeMin = effectiveExitDate < entryDate;

            return isBeforeMin ? {invalidDateRange: true} : null;
        };
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.pendingTransactionSummarySaveSubscription?.unsubscribe();
    }
}
